import { flow } from 'es-toolkit/function';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { player } from 'src/client/player';
import mobileWeb from 'src/common/constants/analytics/categoryActionLabel/mobileWeb';
import connectWithPlayer from 'src/common/decorators/connectWithPlayer';
import { openDownloadAppDialogAndTrack } from '../actions/dialog';
import { logMobileWebActivity } from '../actions/logging';
import { mobileAppStoreRedirectCookie } from '../constants/cookies';
import { ENABLE_PROFILE_PLAY_BUTTON_REDIRECT_TO_APP_STORE } from '../constants/experiments/config';
import { playerStatuses } from '../constants/playerStatuses';
import { useStores } from '../providers';
import { LocationAndLocalizationContext } from '../providers/LocationAndLocalizationProvider';
import { selectBranchUrl } from '../selectors/app';
import { selectExperiment } from '../selectors/config';
import { getCookie, setCookie } from '../utils/cookie';
import { isProfile } from '../utils/guideItemTypes';
import sendToAppDownload from '../utils/sendToAppDownload';
import { withReactHooks } from './withReactHooks';

export default function withSyncedPlayerBehavior(PlayButtonComponent) {
  class ComponentWithSyncedPlayerBehavior extends Component {
    static propTypes = {
      branchUrl: PropTypes.string.isRequired,
      guideItem: PropTypes.object.isRequired,
      syncedPlayerAction: PropTypes.func.isRequired,
      syncedPlayerStatus: PropTypes.string.isRequired,
      isSyncedWithPlayer: PropTypes.bool.isRequired,
      isMediaAdLoaded: PropTypes.bool.isRequired,
      history: PropTypes.object.isRequired,
      isMobile: PropTypes.bool.isRequired,
      logMobileWebActivity: PropTypes.func.isRequired,
      openDownloadAppDialogAndTrack: PropTypes.func.isRequired,
      canScrub: PropTypes.bool,
      enableProfilePlayButtonRedirect: PropTypes.bool,
    };

    static contextType = LocationAndLocalizationContext;

    processAppStoreRedirect(isMobileWebProfile) {
      const {
        branchUrl,
        enableProfilePlayButtonRedirect,
        syncedPlayerStatus,
        logMobileWebActivity: dispatchLogMobileWebActivity,
        isMediaAdLoaded,
      } = this.props;

      const hasCookie = getCookie(mobileAppStoreRedirectCookie.name);

      if (
        isMobileWebProfile &&
        enableProfilePlayButtonRedirect &&
        !hasCookie &&
        syncedPlayerStatus !== playerStatuses.playing &&
        // A recovering session's click stops it, so it isn't a play to send to the app store
        syncedPlayerStatus !== playerStatuses.recovering &&
        !isMediaAdLoaded
      ) {
        setCookie(mobileAppStoreRedirectCookie.name, 1);
        sendToAppDownload(
          branchUrl,
          mobileWeb.labels.playButtonAppStoreRedirect,
          dispatchLogMobileWebActivity,
        );

        return true;
      }
    }

    handlePlayButtonClick = (to) => {
      const {
        guideItem,
        canScrub,
        isMobile,
        isSyncedWithPlayer,
        syncedPlayerAction,
        history,
        openDownloadAppDialogAndTrack: dispatchOpenDownloadAppDialogAndTrack,
      } = this.props;

      const isMobileWebProfile = isMobile && isProfile(guideItem.guideId);
      const redirectAttempted = this.processAppStoreRedirect(isMobileWebProfile);

      if (redirectAttempted) {
        return;
      }

      if (isSyncedWithPlayer) {
        syncedPlayerAction(this.props.playerActions, canScrub);
      } else {
        if (isMobileWebProfile) {
          dispatchOpenDownloadAppDialogAndTrack();
        }

        player.onClick(guideItem);
      }

      // Replace the route so that users navigate back to previous page via
      // browser back button instead of the previously played content.
      if (to?.pathname) {
        history.replace(to);
      }
    };

    render() {
      return (
        <PlayButtonComponent {...this.props} handlePlayButtonClick={this.handlePlayButtonClick} />
      );
    }
  }

  const mapStateToProps = (state) => ({
    branchUrl: selectBranchUrl(state),
    enableProfilePlayButtonRedirect: selectExperiment(
      state,
      ENABLE_PROFILE_PLAY_BUTTON_REDIRECT_TO_APP_STORE,
    ),
  });

  const mapDispatchToProps = {
    openDownloadAppDialogAndTrack,
    logMobileWebActivity,
  };

  return flow(
    connect(mapStateToProps, mapDispatchToProps),
    withReactHooks(() => {
      const { playerStore } = useStores();

      return {
        isMediaAdLoaded: playerStore.metadata()?.isAdBreak || false,
      };
    }),
    withRouter,
    connectWithPlayer,
  )(ComponentWithSyncedPlayerBehavior);
}
